import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { Menu, X, Mountain } from "lucide-react";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on the landing page hero section
  const isLandingPage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // On non-landing pages, always show solid background
  const showSolidBg = !isLandingPage || isScrolled;

  const handleNavClick = (hash) => {
    setMobileMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + hash);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      showSolidBg ? "bg-white border-b border-stone-200 shadow-sm" : "bg-transparent"
    }`} data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Mountain className={`h-8 w-8 ${showSolidBg ? "text-orange-600" : "text-white"}`} />
            <span className={`font-serif text-xl font-medium ${showSolidBg ? "text-slate-800" : "text-white"}`}>
              Steve Hanson
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/#about" 
              onClick={() => handleNavClick("#about")}
              className={`nav-link text-sm font-medium ${showSolidBg ? "text-slate-600 hover:text-slate-900" : "text-white/90 hover:text-white"}`}
            >
              About
            </Link>
            <Link 
              to="/#services" 
              onClick={() => handleNavClick("#services")}
              className={`nav-link text-sm font-medium ${showSolidBg ? "text-slate-600 hover:text-slate-900" : "text-white/90 hover:text-white"}`}
            >
              Services
            </Link>
            <Link 
              to="/coach" 
              className={`nav-link text-sm font-medium ${showSolidBg ? "text-slate-600 hover:text-slate-900" : "text-white/90 hover:text-white"}`}
            >
              AI Coach
            </Link>
            <Link 
              to="/#contact" 
              onClick={() => handleNavClick("#contact")}
              className={`nav-link text-sm font-medium ${showSolidBg ? "text-slate-600 hover:text-slate-900" : "text-white/90 hover:text-white"}`}
            >
              Contact
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin">
                    <Button variant="ghost" className={showSolidBg ? "text-slate-600" : "text-white"} data-testid="admin-link">
                      Admin
                    </Button>
                  </Link>
                )}
                <Button variant="ghost" onClick={logout} className={showSolidBg ? "text-slate-600" : "text-white"} data-testid="logout-btn">
                  Logout
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button variant="ghost" className={showSolidBg ? "text-slate-600" : "text-white"} data-testid="login-link">
                  Login
                </Button>
              </Link>
            )}
            <Link to="/booking">
              <Button className="bg-orange-600 hover:bg-orange-500 text-white" data-testid="book-session-btn">
                Book a Session
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className={showSolidBg ? "text-slate-800" : "text-white"} />
            ) : (
              <Menu className={showSolidBg ? "text-slate-800" : "text-white"} />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white rounded-xl p-4 mb-4 shadow-lg animate-fade-in">
            <div className="flex flex-col gap-4">
              <Link to="/#about" className="text-slate-600 font-medium py-2" onClick={() => handleNavClick("#about")}>About</Link>
              <Link to="/#services" className="text-slate-600 font-medium py-2" onClick={() => handleNavClick("#services")}>Services</Link>
              <Link to="/coach" className="text-slate-600 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>AI Coach</Link>
              <Link to="/#contact" className="text-slate-600 font-medium py-2" onClick={() => handleNavClick("#contact")}>Contact</Link>
              {user ? (
                <>
                  {user.role === "admin" && <Link to="/admin" className="text-slate-600 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Admin</Link>}
                  <Button variant="outline" onClick={() => { logout(); setMobileMenuOpen(false); }}>Logout</Button>
                </>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}><Button variant="outline" className="w-full">Login</Button></Link>
              )}
              <Link to="/booking" onClick={() => setMobileMenuOpen(false)}><Button className="w-full bg-orange-600 hover:bg-orange-500">Book a Session</Button></Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
